import React, { ReactElement } from 'react';
import { Box, Typography, Link } from '@mui/material';
import { Check, ArrowForward } from '@mui/icons-material';
import { gray100, gray80, white, gray20, gray40, green50 } from '../../../constants/colors';

interface FaqItem {
  question: string;
  link: string;
}

interface FaqCardProps {
  faqList: FaqItem[];
  onViewAllClick: () => void;
}

const FaqCard = ({ faqList, onViewAllClick }: FaqCardProps): ReactElement => {
  const styles = {
    container: {
      backgroundColor: white,
      borderRadius: '8px',
      padding: '16px',
      border: `1px solid ${gray40}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: '16px',
      fontWeight: '700',
      color: gray100,
    },
    viewAllLink: {
      fontSize: '16px',
      fontWeight: 400,
      color: gray80,
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      cursor: 'pointer',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    faqItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 0',
      borderBottom: `1px solid ${gray20}`,
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    checkIcon: {
      color: green50,
      fontSize: '20px',
      flexShrink: 0,
    },
    faqText: {
      fontSize: '14px',
      fontWeight: 400,
      color: gray100,
      flex: 1,
      textAlign: 'left',
    },
    faqLink: {
      fontSize: '14px',
      fontWeight: 400,
      color: gray100,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>よくある質問</Typography>
        <Link component="button" onClick={onViewAllClick} sx={styles.viewAllLink}>
          一覧へ
          <ArrowForward sx={{ fontSize: '16px' }} />
        </Link>
      </Box>

      {faqList.map((faq, index) => (
        <Box key={index} sx={styles.faqItem}>
          <Check sx={styles.checkIcon} />
          <Link href={faq.link} target="_blank" rel="noopener noreferrer" sx={styles.faqLink}>
            <Typography sx={styles.faqText}>{faq.question}</Typography>
          </Link>
        </Box>
      ))}
    </Box>
  );
};

export default FaqCard;
